import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useNavigationType, useLocation } from 'react-router-dom';
import { billApi } from '@root/api';
import Toast from '@root/components/toast';
import useBillAddressState from '../useBillAddress';
import { getQueryString } from '@root/shared';

const useBillAddress = () => {
  const navigate = useNavigate();
  const navigateType = useNavigationType();
  const { t } = useTranslation();
  const [requesting, setRequesting] = useState(false);
  const [postalErrMsg, setPostalErrMsg] = useState('');
  const { pathname } = useLocation();
  const {
    data,
    modifyData,
    setData,
    setModifyData,
    getBillAddress,
    resetModifyData,
  } = useBillAddressState();

  const getAddressRef = useRef<any>({
    postal: null,
    countryCode: null,
    error: '',
  });

  // 返回在购买过程中跳过来的
  const isBack = getQueryString('back');

  const finalData = useMemo(
    () => ({ countryCode: 'USA', ...data, ...modifyData }),
    [data, modifyData],
  );

  const { countryCode, postal, county, address } = finalData;

  // 根据国家和邮编获取地址
  const getAddress = useCallback(() => {
    const _same =
      getAddressRef.current.postal === postal &&
      getAddressRef.current.countryCode === countryCode;
    if (_same || !postal || !countryCode) {
      if (_same && getAddressRef.current.error) {
        Toast.destroy();
        Toast.info(getAddressRef.current.error);
      }
      return;
    }

    billApi
      .getAddress(countryCode, postal)
      .then((data: any) => {
        Toast.destroy();
        if (data) {
          setModifyData(data);
          getAddressRef.current = {
            countryCode,
            postal,
          };
        } else {
          Toast.info(t('bill_zip_code_error'));
          getAddressRef.current = {
            countryCode,
            postal,
            error: t('bill_zip_code_error'),
          };
        }
      })
      .catch(() => {
        Toast.destroy();
        Toast.info(t('bill_zip_code_error'));
        getAddressRef.current = {
          countryCode,
          postal,
          error: t('bill_zip_code_error'),
        };
      });
  }, [countryCode, postal, setModifyData, t]);

  const onSelectCountry = useCallback(() => {
    navigate('/billAddress/SelectCountry');
  }, [navigate]);

  const onValidate = useCallback(
    (key: string, value: string) => {
      if (key === 'postal') {
        if (!postal) {
          return t('bill_zip_code_empty');
        }
        if (countryCode === 'USA') {
          const msg = /^[\d]{5}$/.test(value)
            ? ''
            : t('bill_zip_code_type_error');
          setPostalErrMsg(msg);
          return msg;
        }

        switch (countryCode) {
          case 'AUT':
          case 'BEL':
          case 'DNK':
          case 'HUN':
          case 'LUX':
          case 'NLD':
          case 'NZL': {
            const msg = /^[\d]{1,4}$/.test(value)
              ? ''
              : t('bill_check_code_tips_1');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'FIN':
          case 'FRA':
          case 'DEU':
          case 'GRC':
          case 'ITA':
          case 'POL':
          case 'ESP':
          case 'ARE': {
            const msg = /^[\d]{1,5}$/.test(value)
              ? ''
              : t('bill_check_code_tips_2');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'ROU': {
            const msg = /^[\d]{1,6}$/.test(value)
              ? ''
              : t('bill_check_code_tips_8');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'SWE': {
            // 6位 -- 数字加空格
            const msg = /^[\d\s]{1,6}$/.test(value)
              ? ''
              : t('bill_check_code_tips_6');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'IRL': {
            // 7位 -- 字母+数字组合
            const msg = /^[A-Z0-9]{1,7}$/.test(value)
              ? ''
              : t('bill_check_code_tips_9');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'CAN': {
            // 大写字母+空格+数字 7
            const msg = /^[A-Z\s\d]{1,7}$/.test(value)
              ? ''
              : t('bill_check_code_tips_3');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'GBR': {
            // 大写字母+空格+数字 8
            const msg = /^[A-Z\s\d]{1,8}$/.test(value)
              ? ''
              : t('bill_check_code_tips_4');
            setPostalErrMsg(msg);
            return msg;
          }
          case 'OTHER': {
            // 大写字母+空格+数字 10
            const msg = /^[A-Z\s\d]{1,10}$/.test(value)
              ? ''
              : t('bill_check_code_tips_5');
            setPostalErrMsg(msg);
            return msg;
          }
          default:
            return t('bill_zip_code_empty');
        }
      }
    },
    [countryCode, postal, t],
  );

  const onChangeAddress = useCallback(
    (e: any) => {
      const v = e.target.value;
      setModifyData({ address: v });
    },
    [setModifyData],
  );

  const onChangeZipCode = useCallback(
    (e: any) => {
      const v = e.target.value;
      setModifyData({
        postal: v,
        county: '',
        city: '',
        state: '',
      });
      onValidate('postal', v);
      // 变更ZIP 重置缓存
      if (getAddressRef.current.countryCode) {
        getAddressRef.current = {
          countryCode: null,
          postal: null,
          error: null,
        };
      }
    },
    [onValidate, setModifyData],
  );

  const saveDisabled = useMemo(() => {
    const _address = countryCode === 'USA' ? county : address;

    if (!(countryCode && postal && _address)) {
      return true;
    } else if (
      data.id &&
      data.countryCode === countryCode &&
      data.postal === postal
    ) {
      if (countryCode === 'USA') {
        return true;
      }

      if (address === data.address) {
        return true;
      }
    }
    return false;
  }, [
    address,
    countryCode,
    county,
    data.address,
    data.countryCode,
    data.id,
    data.postal,
    postal,
  ]);

  const onSave = useCallback(() => {
    if (saveDisabled) {
      return;
    }

    setRequesting(true);

    const msg = onValidate('postal', postal);
    if (msg) {
      Toast.destroy();
      Toast.info(msg);
      setRequesting(false);
      return;
    }

    if (countryCode === 'USA' && !county) {
      Toast.destroy();
      Toast.info(t('bill_zip_code_tips'));
      setRequesting(false);
      return;
    }

    if (countryCode !== 'USA' && !address) {
      Toast.destroy();
      Toast.info(t('bill_address_empty'));
      setRequesting(false);
      return;
    }

    const apiFunc = data.id
      ? billApi.updateUserBillInfo
      : billApi.addUserBillInfo;

    apiFunc({
      countryCode: countryCode,
      postal: postal,
      id: data.id || undefined,
      address: countryCode === 'USA' ? undefined : address,
    })
      .then(() => {
        Toast.destroy();
        setRequesting(false);
        getBillAddress();
        if (!pathname.startsWith('/package/renewal')) {
          Toast.info(data.id ? t('update_successful') : t('save_successful'));
        }
        if (isBack) {
          navigate(-1);
        }
      })
      .catch(() => {
        Toast.destroy();
        setRequesting(false);
        Toast.info(t('save_failed'));
      });
  }, [
    address,
    countryCode,
    county,
    data.id,
    getBillAddress,
    isBack,
    navigate,
    onValidate,
    pathname,
    postal,
    saveDisabled,
    t,
  ]);

  useEffect(() => {
    if (navigateType === 'PUSH') {
      resetModifyData();
    }
  }, [navigateType, resetModifyData, setModifyData]);

  useEffect(() => {
    onValidate('postal', postal);
  }, [onValidate, postal]);

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  return {
    state: {
      saveDisabled,
      finalData,
      postalErrMsg,
      requesting,
    },
    action: {
      setData,
      getAddress,
      onSelectCountry,
      onChangeZipCode,
      onSave,
      onChangeAddress,
    },
  };
};

export default useBillAddress;
